import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faRoute } from '@fortawesome/pro-light-svg-icons/faRoute';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useStreamingPlaySources } from '../../hooks/useStreamingPlaySources';
import { useStreamingSearchParam } from '../../hooks/useStreamingSearchParam';
import { useStreamingPeriod } from '../../hooks/useStreamingPeriod';
import { pickedOption, playSourceTiles } from '../../periodSections';
import { sourceLook } from '../../streamingSources';
import { formatDay } from '../../streamingFormat';
import { playSourcesMessages as messages } from '../../playSourcesMessages';
import { streamingPeriodMessages } from '../../streamingPeriodMessages';
import { StreamingPlanLockCard } from '../../parts/StreamingPlanLockCard';
import { SectionSourceSwitch } from '../../period/SectionSourceSwitch';
import { SectionMonthLine } from '../../period/SectionMonthLine';
import { SectionNoneCard } from '../../period/SectionNoneCard';
import { PlaySourceTiles } from './PlaySourceTiles';
import { PlaySourceRows } from './PlaySourceRows';
import { PlaySourceMonthBars } from './PlaySourceMonthBars';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PlaySourcesBodyProps {
  accountId: number;
  days: number;
}

/** The read of Play sources: the switch, the month line, the tiles, the rows and the month bars. */
const PlaySourcesBodyComponent: React.FC<PlaySourcesBodyProps> = ({ accountId, days }) => {
  const intl = useIntl();
  const period = useStreamingPeriod();
  const [source, setSource] = useStreamingSearchParam('source');
  const { data, isLoading, error, retry } = useStreamingPlaySources({ accountId, days, source, skip: false });
  const tiles = useMemo(() => playSourceTiles(data?.rows ?? []), [data]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (data === undefined) {
    return null;
  }
  if (data.locked === 'plan') {
    return (
      <StreamingPlanLockCard
        feature={SMLUpgradeFeature.streamingSplit}
        title={streamingPeriodMessages.planTitle}
        testId="streaming-playSources-plan-lock"
      />
    );
  }

  const picked = pickedOption(data.sources, data.source);
  const sourceName = picked === undefined ? '' : sourceLook(picked.integrationType).name;
  const storeName = data.store ?? (picked === undefined ? '' : sourceLook(picked.integrationType).shortName);
  const start = formatDay(intl, data.range.from);
  const end = formatDay(intl, data.range.to);

  return (
    <div className={shared.page} data-testid="streaming-section-playSources">
      <SectionSourceSwitch options={data.sources} picked={data.source} onPick={setSource} />
      <SectionMonthLine text={intl.formatMessage(messages.monthLine, { source: sourceName })} months={data.months} />
      {!data.available ? (
        <p className={shared.faint}>{intl.formatMessage(streamingPeriodMessages.notAvailable)}</p>
      ) : null}
      {data.rows.length > 0 ? (
        <>
          <PlaySourceTiles tiles={tiles} />
          <div className={shared.blocksTwo}>
            <PlaySourceRows rows={data.rows} storeName={storeName} sourceName={sourceName} />
            <PlaySourceMonthBars byMonth={data.byMonth} />
          </div>
        </>
      ) : (
        <SectionNoneCard
          icon={faRoute}
          testId="streaming-playSources-none"
          title={
            data.months.length === 0
              ? intl.formatMessage(streamingPeriodMessages.noWholeMonthTitle, { start, end })
              : intl.formatMessage(messages.noRowsTitle, { source: sourceName, start, end })
          }
          text={
            data.months.length === 0 ? intl.formatMessage(messages.noWholeMonthText, { source: sourceName }) : undefined
          }
        >
          {days < 90 ? (
            <SMLButton variant="primary" onClick={() => period.setDays(90)}>
              {intl.formatMessage(streamingPeriodMessages.pickDays, { days: 90 })}
            </SMLButton>
          ) : null}
        </SectionNoneCard>
      )}
      {data.storesWithoutSplit.length > 0 ? (
        <p className={shared.footnote} data-testid="streaming-playSources-without-split">
          {intl.formatMessage(messages.withoutSplit, {
            source: sourceName,
            store: storeName,
            stores: intl.formatList(data.storesWithoutSplit, { type: 'conjunction' }),
          })}
        </p>
      ) : null}
    </div>
  );
};

export const PlaySourcesBody = TypedMemo(PlaySourcesBodyComponent);
